import React, { useEffect, useState, useContext, useRef } from 'react';
import proptypes from 'prop-types';
const { bool, number, oneOf, oneOfType, shape, string } = proptypes;
import { VideoPlayerContext } from '../contexts/VideoPlayerContext';
import {
  setVolume,
  toggleMuteOnVideo,
  getPlayerType,
  PlayerTypes
} from '../helpers/videoPlayerHelpers';
import { VideoStill } from './VideoStill';
import { S3Player } from './players/S3Player';
import { YouTubePlayer } from './players/YouTubePlayer';
import { BrightCovePlayer } from './players/BrightCovePlayer';

export const VideoPlayer = ({
  analyticsData,
  aspectRatio,
  autoPlay,
  className,
  enableB2CRadius,
  height,
  playerId,
  videoId,
  videoSrc,
  videoStill,
  width,
  source,
  shouldUseContentPlayer
}) => {
  const playerRef = useRef();
  const {
    autoPlayInViewport, observer
  } = useContext(VideoPlayerContext) || {};

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('video-player.ready');
  });
  const [enableVideoStill, setEnableVideoStill] = useState(!!videoStill);
  const playerType = getPlayerType({ videoSrc, source, videoId });

  useEffect(() => {
    if (
      autoPlayInViewport
      && !!playerRef.current
      && typeof window !== 'undefined'
      && observer
    ) {
      observer.observe(playerRef.current);
    }

    return () => {
      if (observer) {
        observer.unobserve(playerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleEqualizeVolume = ({ output }) => {
      if (output.id !== playerRef.current?.id) {
        setVolume({ frame: playerRef.current, volume: output.volume });
        toggleMuteOnVideo({ frame: playerRef.current, mute: output.mute });
      }
    };

    if (!enableVideoStill) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('video-player.equalize-volume', handleEqualizeVolume);
    }

    return () => {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off('video-player.equalize-volume', handleEqualizeVolume);
    };
  }, [enableVideoStill]);

  if (enableVideoStill) {
    return (
      <VideoStill
        width={width}
        height={height}
        videoStill={videoStill}
        onPlayButtonClick={() => setEnableVideoStill(false)}
      />
    );
  }

  if (playerType === PlayerTypes.S3) {
    return (
      <S3Player
        className={className}
        height={height}
        width={width}
        videoSrc={videoSrc}
        ref={playerRef}
      />
    );
  }

  if (playerType === PlayerTypes.YouTube) {
    return (
      <YouTubePlayer
        analyticsData={analyticsData}
        enableB2CRadius={enableB2CRadius}
        autoPlay={autoPlay}
        className={className}
        height={height}
        playerId={playerId}
        videoSrc={videoSrc}
        videoStill={videoStill}
        width={width}
        ref={playerRef}
      />
    );
  }

  if (playerType === PlayerTypes.BrightCove) {
    return (
      <BrightCovePlayer
        analyticsData={analyticsData}
        aspectRatio={aspectRatio}
        autoPlay={autoPlay}
        className={className}
        enableB2CRadius={enableB2CRadius}
        height={height}
        videoId={videoId}
        videoStill={videoStill}
        width={width}
        ref={playerRef}
        shouldUseContentPlayer={shouldUseContentPlayer}
      />
    );
  }

  return null;
};

VideoPlayer.propTypes = {
  analyticsData: shape({}),
  aspectRatio: oneOf([16 / 9, 4 / 3]),
  autoPlay: bool,
  className: string,
  enableB2CRadius: bool,
  height: number,
  playerId: string,
  source: string,
  shouldUseContentPlayer: bool,
  videoId: oneOfType([string, number]),
  videoSrc: string,
  videoStill: string,
  width: number
};

VideoPlayer.defaultProps = {
  analyticsData: null,
  aspectRatio: 16 / 9,
  autoPlay: false,
  className: null,
  enableB2CRadius: false,
  height: null,
  playerId: null,
  source: null,
  shouldUseContentPlayer: false,
  videoId: null,
  videoSrc: null,
  videoStill: null,
  width: null
};